import { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles, X, Send, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { useQuery } from 'react-query';
import { aiAssistantAPI, streamAssistantChat } from '@shared/apiClient';
import { useShell } from '../../shell/ShellContext';
import PendingActionCard from './PendingActionCard';

const WELCOME_MESSAGE = {
  role: 'assistant',
  content: "Hi! I'm the K&R Hub Assistant. I can answer how-to questions, manage users and settings, and look up live data — service calls, customers, tech metrics, and the parts queue. What can I help with?",
};

const EXAMPLE_PROMPTS = [
  "Give me today's dispatch summary",
  'What service calls are unassigned?',
  'Show tech metrics for this year',
  'How do I auto-schedule calls for next week?',
  'Run auto-schedule optimizing for least travel time',
  'How do I send a C Spire invoice?',
  'How do I create a smart folder in Invoicing?',
];

export default function AssistantPanel() {
  const { assistantOpen, setAssistantOpen } = useShell();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamContent, setStreamContent] = useState('');
  const [activeTool, setActiveTool] = useState(null);
  const [pendingActions, setPendingActions] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  const { data: statusData } = useQuery(
    'aiAssistantStatus',
    () => aiAssistantAPI.getStatus(),
    { enabled: assistantOpen, staleTime: 60000 }
  );

  const configured = statusData?.data?.configured ?? true;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (assistantOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [assistantOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamContent, pendingActions, scrollToBottom]);

  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || streaming) return;

    const userMessage = { role: 'user', content: trimmed };
    const history = [...messages, userMessage];
    setMessages(history);
    setInput('');
    setStreaming(true);
    setStreamContent('');
    setActiveTool(null);
    setPendingActions([]);

    const controller = new AbortController();
    abortRef.current = controller;

    let accumulated = '';

    try {
      await streamAssistantChat(
        history.map(({ role, content }) => ({ role, content })),
        {
          signal: controller.signal,
          onEvent: (event, data) => {
            switch (event) {
              case 'token':
                accumulated += data.content;
                setStreamContent(accumulated);
                break;
              case 'tool_status':
                if (data.status === 'running') {
                  setActiveTool(data.message);
                } else {
                  setActiveTool(null);
                }
                break;
              case 'pending_action':
                setPendingActions((prev) => [...prev, data]);
                break;
              case 'error':
                accumulated += `\n\nError: ${data.message}`;
                setStreamContent(accumulated);
                break;
              default:
                break;
            }
          }
        }
      );

      if (accumulated) {
        setMessages((prev) => [...prev, { role: 'assistant', content: accumulated }]);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        const errorMsg = err.message || 'Failed to get a response';
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: `Sorry, something went wrong: ${errorMsg}` }
        ]);
      }
    } finally {
      setStreaming(false);
      setStreamContent('');
      setActiveTool(null);
      abortRef.current = null;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleActionConfirmed = (data) => {
    const note = `[Action completed: ${data.summary}]`;
    setMessages((prev) => [...prev, { role: 'user', content: note, hidden: true }]);
  };

  const handleClose = () => {
    abortRef.current?.abort();
    setAssistantOpen(false);
  };

  if (!assistantOpen) return null;

  const displayMessages = messages.length === 0 ? [WELCOME_MESSAGE] : messages;

  return (
    <div className="fixed inset-0 z-[90] flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-gray-900/30"
        aria-label="Close assistant"
        onClick={handleClose}
      />

      <div className="relative w-full max-w-md h-full bg-surface border-l border-surface-border shadow-xl flex flex-col">
        <header className="flex items-center justify-between px-4 py-3 border-b border-surface-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary-600" />
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Hub Assistant</h2>
              {!configured && (
                <p className="text-2xs text-amber-600">Not configured — set OPENAI_API_KEY</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 rounded-md hover:bg-surface-hover text-gray-500"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
          {displayMessages.map((msg, idx) => {
            if (msg.hidden) return null;
            const isUser = msg.role === 'user';
            return (
              <div
                key={idx}
                className={clsx('flex', isUser ? 'justify-end' : 'justify-start')}
              >
                <div
                  className={clsx(
                    'max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap',
                    isUser
                      ? 'bg-primary-600 text-white'
                      : 'bg-surface-secondary border border-surface-border text-gray-800'
                  )}
                >
                  {msg.content}
                </div>
              </div>
            );
          })}

          {streaming && streamContent && (
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap bg-surface-secondary border border-surface-border text-gray-800">
                {streamContent}
              </div>
            </div>
          )}

          {streaming && !streamContent && activeTool && (
            <div className="flex justify-start">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-surface-secondary border border-surface-border text-xs text-gray-600">
                <Loader2 className="h-3 w-3 animate-spin" />
                {activeTool}
              </div>
            </div>
          )}

          {streaming && !streamContent && !activeTool && (
            <div className="flex justify-start">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-surface-secondary border border-surface-border text-xs text-gray-500">
                <Loader2 className="h-3 w-3 animate-spin" />
                Thinking…
              </div>
            </div>
          )}

          {pendingActions.map((action) => (
            <PendingActionCard
              key={action.id}
              action={action}
              onConfirmed={handleActionConfirmed}
            />
          ))}

          <div ref={messagesEndRef} />
        </div>

        {messages.length === 0 && !streaming && (
          <div className="px-4 pb-2 flex flex-wrap gap-1.5 flex-shrink-0">
            {EXAMPLE_PROMPTS.map((prompt) => (
              <button
                key={prompt}
                type="button"
                onClick={() => sendMessage(prompt)}
                disabled={!configured}
                className="text-2xs px-2 py-1 rounded-md border border-surface-border bg-surface-secondary text-gray-600 hover:bg-surface-hover disabled:opacity-50"
              >
                {prompt}
              </button>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="px-4 py-3 border-t border-surface-border flex-shrink-0">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              placeholder={configured ? 'Ask a question or request an action…' : 'Assistant not configured'}
              disabled={streaming || !configured}
              rows={2}
              className="flex-1 resize-none rounded-lg border border-surface-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={streaming || !input.trim() || !configured}
              className="p-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 flex-shrink-0"
            >
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-2xs text-gray-400 mt-1.5">Actions require your confirmation before executing.</p>
        </form>
      </div>
    </div>
  );
}
